import { Link } from "@tanstack/solid-router";
import type { JSX } from "solid-js";

/**
 * Render an entity reference as a Link to its detail page, given the
 * audit/search-flavoured `entity_type` + bare-ULID `entity_id`.
 *
 * Returns `null` when the entity type has no dedicated detail page or
 * lacks the context to route — e.g. custom_record needs the parent
 * object's `key` passed in via `objectKey`; absent that, returns null
 * and the caller falls back to a non-link.
 *
 * The bare ULID stored in audit/search rows lacks its type prefix; the
 * `prefix_` part is re-applied here so TanStack Router's typed `to`
 * accepts the resulting parameter.
 */
export function entityDetailLink(
  wsKey: string,
  entityType: string,
  entityId: string,
  children: JSX.Element,
  objectKey?: string | null,
): JSX.Element | null {
  if (entityType === "account") {
    return (
      <Link
        to="/workspaces/$wsKey/accounts/$accId"
        params={{ wsKey, accId: `acc_${entityId}` }}
        class="link link-primary"
      >
        {children}
      </Link>
    );
  }
  if (entityType === "contact") {
    return (
      <Link
        to="/workspaces/$wsKey/contacts/$conId"
        params={{ wsKey, conId: `con_${entityId}` }}
        class="link link-primary"
      >
        {children}
      </Link>
    );
  }
  if (entityType === "deal") {
    return (
      <Link
        to="/workspaces/$wsKey/deals/$dealId"
        params={{ wsKey, dealId: `deal_${entityId}` }}
        class="link link-primary"
      >
        {children}
      </Link>
    );
  }
  if (entityType === "custom_record" && objectKey) {
    return (
      <Link
        to="/workspaces/$wsKey/custom/$objectKey/$recordId"
        params={{ wsKey, objectKey, recordId: `rec_${entityId}` }}
        class="link link-primary"
      >
        {children}
      </Link>
    );
  }
  return null;
}
